import { getCuratorGroupsWithUsers, getProductIdsPromoted, getTaste, isShopper } from './user_helpers';
import { isValidDomain } from './formatting';

export const getOnboardingStepsEnum = ({ user }) => {
  if (isShopper(user)) {
    return ['FIRST_USER_PRODUCT', 'FIRST_CIRCLE', 'TASTE_PROFILE'];
  }
  return [];
};

export const onboardingConstants = {
  MIN_PRODUCTS_FOR_TASTE: 25,
  MINUTES_BETWEEN_TASTE_RUNS: 60
};

export const getNumOnboardingStepsComplete = ({ user, shop }) => {
  return getOnboardingStepsEnum({ user }).filter(stepEnum => isOnboardingStepComplete(stepEnum, { user, shop })).length;
};

export const hasCompletedOnboarding = ({ user, shop }) => {
  const onboardingStepsEnum = getOnboardingStepsEnum({ user });
  const numComplete = getNumOnboardingStepsComplete({ user, shop });
  return numComplete >= onboardingStepsEnum.length;
};

export const canBuildTasteProfile = user => {
  const numProductsPromoted = getProductIdsPromoted(user).length;
  return numProductsPromoted >= onboardingConstants.MIN_PRODUCTS_FOR_TASTE;
};

export const isOnboardingStepComplete = (stepEnum, { user, shop }) => {
  if (!user) {
    console.error('isOnboardingStepComplete: User is required');
    return false;
  }
  const curatorGroupsWithUsers = getCuratorGroupsWithUsers(user);
  const numProductsPromoted = getProductIdsPromoted(user).length;
  switch (stepEnum) {
    case 'FIRST_CIRCLE':
      return curatorGroupsWithUsers.length > 0;
    case 'FIRST_USER_PRODUCT':
      const hasProductsPromoted = numProductsPromoted > 0;
      return hasProductsPromoted;
    case 'TASTE_PROFILE':
      const tasteProfile = !!getTaste(user)?.taste_profile;
      return !!tasteProfile;
    default:
      return false;
  }
};

export const getNumProductsBeforeTasteProfile = user => {
  const numProductsPromoted = getProductIdsPromoted(user).length;
  return onboardingConstants.MIN_PRODUCTS_FOR_TASTE - numProductsPromoted;
};

/*
  Tooltipping helpers
*/
export const tooltipKeys = {
  ADD_TO_CIRCLE_FROM_SHOP_PAGE_HEADER: 'shop-page-header-add-to-circle',
  ADD_TO_WISHLIST_FROM_PRODUCT_PAGE: 'add-product-training-tooltip-product-page'
};

export const shouldShowTooltip = (trainingKey, { user, shop }) => {
  return (
    {
      [tooltipKeys.ADD_TO_CIRCLE_FROM_SHOP_PAGE_HEADER]: () => {
        // Handled in ShopPageHeader.js
        return true;
      },
      [tooltipKeys.ADD_TO_WISHLIST_FROM_PRODUCT_PAGE]: () => {
        const numProductsPromoted = getProductIdsPromoted(user).length;
        return numProductsPromoted === 0;
      }
    }[trainingKey] ||
    (() => {
      return false;
    })
  )();
};

export const brandOnboardingPanelIsViewable = panel => !panel.isHidden() && panel.canView();
export const brandOnboardingPanelHasBeenViewed = (onboarding, panelName) => {
  if (!Array.isArray(onboarding?.viewedPanels)) return false;

  const panelHasBeenViewed = onboarding?.viewedPanels?.includes(panelName);
  return panelHasBeenViewed;
};

export const brandOnboardingGetDomainToSetup = onboarding => {
  const { domainsToSetup } = onboarding || {};
  if (!domainsToSetup?.length) return null;

  const domainsNeedingSetup = domainsToSetup.filter(d => !brandOnboardingDomainSetupIsComplete(onboarding, d));
  if (!domainsNeedingSetup.length) return null;
  else return domainsNeedingSetup[0];
};

export const brandOnboardingDomainSetupIsComplete = (onboarding, domainToSetup) => {
  if (!onboarding || !domainToSetup) return false;
  const { skipShopifySetup, connectedShopify, domain } = domainToSetup;
  if (!isValidDomain(domain)) return false;

  const usesShopify = onboarding.usesShopify;
  const settingUpOnShopmyNetwork = onboarding.integratingMerchant === 'shopmy';

  const shopifySetupComplete = usesShopify && (connectedShopify || skipShopifySetup);
  const networkSetupComplete = true; // hardcoded for phase 1 & 2 release

  if (!settingUpOnShopmyNetwork && !usesShopify) return true;
  else if (settingUpOnShopmyNetwork && !usesShopify) return networkSetupComplete;
  else if (!settingUpOnShopmyNetwork && usesShopify) return shopifySetupComplete;
  else if (settingUpOnShopmyNetwork && usesShopify) return shopifySetupComplete && networkSetupComplete;

  return false;
};

export const brandOnboardingStatuses = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETE: 'COMPLETE'
};
